import { Button } from "@swot/base-components";
import { normalizeApiErrorForUi } from "../../core/api/apiErrorNormalizer.js";
import { type CmtsApiError } from "../../core/errors/index.js";
import { SwotAlert, SwotConflictState, SwotMissingEntity, SwotPermissionDenied, SwotRetryPanel, SwotSupportDetails } from "@swot-shared/feedback/index.js";
import { DependencyErrors } from "./DependencyErrors.js";

export type ApiErrorPanelProps = {
  readonly error: CmtsApiError | unknown | null;
  readonly title?: string | undefined;
  readonly onRetry?: (() => void) | undefined;
  readonly onRefresh?: (() => void) | undefined;
  readonly onBack?: (() => void) | undefined;
  readonly compact?: boolean | undefined;
};

export function ApiErrorPanel(props: ApiErrorPanelProps) {
  if (props.error === null || props.error === undefined) return null;
  const error = normalizeUiSafeError(props.error);
  if (error.code === "request_aborted") return null;

  if (error.category === "authorization") {
    return (
      <section className={props.compact ? "cmts-api-error-panel cmts-api-error-panel--compact" : "cmts-api-error-panel"}>
        <SwotPermissionDenied title={props.title ?? "Permission denied"} message={error.message} permission={error.permission} actionCode={error.actionCode} onBack={props.onBack} />
        <Remediation text={error.remediation} />
        <SwotSupportDetails correlationId={error.correlationId} technicalDetails={error.technicalDetails} diagnostics={error.safeDiagnostics} />
      </section>
    );
  }

  if (error.category === "route" || error.httpStatus === 404) {
    return (
      <section className={props.compact ? "cmts-api-error-panel cmts-api-error-panel--compact" : "cmts-api-error-panel"}>
        <SwotMissingEntity title={props.title ?? "Record not found"} message={error.message} onBack={props.onBack} onRefresh={props.onRefresh} />
        <Remediation text={error.remediation} />
        <SwotSupportDetails correlationId={error.correlationId} technicalDetails={error.technicalDetails} diagnostics={error.safeDiagnostics} />
      </section>
    );
  }

  if (error.category === "dependency" && error.dependencyErrors.length > 0) {
    return (
      <section className={props.compact ? "cmts-api-error-panel cmts-api-error-panel--compact" : "cmts-api-error-panel"}>
        <DependencyErrors errors={error.dependencyErrors} mode="panel" />
        <Remediation text={error.remediation} />
        <SwotSupportDetails correlationId={error.correlationId} technicalDetails={error.technicalDetails} diagnostics={error.safeDiagnostics} />
      </section>
    );
  }

  if (error.category === "concurrency" || error.category === "idempotency" || error.httpStatus === 409) {
    return (
      <section className={props.compact ? "cmts-api-error-panel cmts-api-error-panel--compact" : "cmts-api-error-panel"}>
        <SwotConflictState title={props.title ?? conflictTitle(error.code)} message={error.message} onRefresh={props.onRefresh} />
        <Remediation text={error.remediation} />
        {error.retryable && props.onRetry ? <SwotRetryPanel title="Retry after refresh" message="Retry only after confirming the latest backend state." onRetry={props.onRetry} /> : null}
        <SwotSupportDetails correlationId={error.correlationId} technicalDetails={error.technicalDetails} diagnostics={error.safeDiagnostics} />
      </section>
    );
  }

  const tone = error.severity === "warning" ? "warning" : error.severity === "info" ? "info" : "error";
  const safeDisplayMessage = error.message;
  return (
    <section className={props.compact ? "cmts-api-error-panel cmts-api-error-panel--compact" : "cmts-api-error-panel"}>
      <SwotAlert tone={tone} title={props.title ?? titleFor(error)}>
        <p>{safeDisplayMessage}</p>
        <Remediation text={error.remediation} />
        {error.permission ? <p><strong>Permission:</strong> {error.permission}</p> : null}
        {error.actionCode ? <p><strong>Action:</strong> {error.actionCode}</p> : null}
        {error.retryable && props.onRetry ? <Button label="Retry" variant="secondary" onClick={props.onRetry} /> : null}
        {props.onRefresh ? <Button label="Refresh" variant="secondary" onClick={props.onRefresh} /> : null}
        <SwotSupportDetails correlationId={error.correlationId} technicalDetails={error.technicalDetails} diagnostics={error.safeDiagnostics} />
      </SwotAlert>
    </section>
  );
}

function Remediation(props: { readonly text: string }) {
  return <p className="cmts-api-error-panel__remediation"><strong>Recommended action:</strong> {props.text}</p>;
}

function normalizeUiSafeError(value: unknown): CmtsApiError {
  const normalized = normalizeApiErrorForUi(value);
  return {
    ...normalized,
    technicalDetails: undefined,
    safeDiagnostics: sanitizeDiagnostics(normalized.safeDiagnostics),
  };
}

function sanitizeDiagnostics(value: CmtsApiError["safeDiagnostics"]): CmtsApiError["safeDiagnostics"] {
  if (!value) return undefined;
  const allowedKeys = new Set(["code", "category", "severity", "retryable", "httpStatus", "operation", "route", "correlationId"]);
  const result: Record<string, string | number | boolean | null> = {};
  for (const [key, item] of Object.entries(value)) {
    if (!allowedKeys.has(key)) continue;
    if (typeof item === "string" && /stack trace|node_modules|webpack|vite|System\.|Microsoft\.|Exception:|\bat\s+\S+\.(?:tsx?|jsx?|cs):\d+:\d+/i.test(item)) continue;
    result[key] = item;
  }
  return Object.keys(result).length ? result : undefined;
}

function titleFor(error: CmtsApiError): string {
  if (error.category === "field_validation" || error.category === "validation") return "Validation issue";
  if (error.category === "evidence") return "Evidence issue";
  if (error.category === "network") return "Network issue";
  if (error.category === "persistence") return "Backend persistence issue";
  return "CMTS request failed";
}

function conflictTitle(code: string): string {
  if (code === "stale_snapshot") return "Stale record snapshot";
  if (code === "idempotency_conflict") return "Duplicate submission check";
  return "Conflict detected";
}

export { ApiErrorPanel as CmtsStructuredApiErrorPanel };
